/**
 * Module 'session-control': the session drawer's data beyond the transcript.
 *
 *   - Origin. A session is `launched` when Orgus started it, `discovered` when Orgus
 *     found it running (a terminal, an IDE). Discovered sessions are read-only.
 *   - Claims. An operator can claim a discovered session: an audited record that a person
 *     has taken responsibility for it. Claiming injects nothing into the running session
 *     (ADR 0001: no hooks in watched sessions); it unlocks Stop from Orgus, nothing else.
 *   - Messages. A session Orgus launched headless reads Claude Code stream-json input on
 *     its stdin, so a person can send it further prompts. Each one is audited and kept.
 *   - Events. A structured, filterable view of one session's transcript (prompts, tool calls,
 *     subagents, approvals, compactions), read on demand by the agent.
 *
 * Browser-safe.
 */
import type { Id, Session, Ts, UiErrorShape } from '../index.ts';

// ---------------------------------------------------------------------------
// Origin
// ---------------------------------------------------------------------------

/** Architecture spec section 4: discovered (found running) or launched (started by Orgus). */
export type SessionOrigin = 'discovered' | 'launched';

/** The core field says 'orgus' for sessions Orgus started and 'interactive' for the rest. */
export function sessionOrigin(s: Pick<Session, 'origin'>): SessionOrigin {
  return s.origin === 'orgus' ? 'launched' : 'discovered';
}

export const SESSION_ORIGIN_LABEL: Record<SessionOrigin, string> = {
  discovered: 'Discovered',
  launched: 'Launched here',
};

// ---------------------------------------------------------------------------
// Claims
// ---------------------------------------------------------------------------

export interface SessionClaim {
  sessionId: Id;
  machineId: Id;
  /** The actor id that claimed it ('owner', a GitHub login, 'agent:<name>'). */
  by: string;
  at: Ts;
  note?: string;
  /** The audit entry that recorded the claim. */
  auditId: Id;
}

/** Roles that may claim a session, send a message, or release someone else's claim (admin+). */
export const SESSION_CLAIM_ROLES: readonly string[] = ['owner', 'admin', 'operator'];
export const SESSION_RELEASE_ANY_ROLES: readonly string[] = ['owner', 'admin'];

/** POST /api/sessions/:id/claim */
export interface ClaimRequest {
  /** The person confirmed the dialog that explains what a claim does. */
  confirm: true;
  note?: string;
}

// ---------------------------------------------------------------------------
// Messages to launched headless sessions
// ---------------------------------------------------------------------------

/**
 * sending    the hub is handing it to the machine;
 * sent       the agent wrote it to the session's stdin;
 * delivered  Claude Code read it (it echoed it back on its output);
 * failed     the machine refused it or could not write it (`error` says why).
 */
export type SessionMessageState = 'sending' | 'sent' | 'delivered' | 'failed';

export const SESSION_MESSAGE_STATE_LABEL: Record<SessionMessageState, string> = {
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Read by Claude',
  failed: 'Not sent',
};

export interface SessionMessage {
  id: Id;
  sessionId: Id;
  machineId: Id;
  text: string;
  by: string;
  at: Ts;
  state: SessionMessageState;
  deliveredAt?: Ts;
  error?: string;
  auditId?: Id;
  /** Only in state.ext: `text` is a preview; GET /api/sessions/:id/messages has the whole text. */
  clipped?: true;
  /** The message went to a fresh continuation of this session, from its mission's handoff (ADR 0062 decision 2a). */
  freshSessionId?: Id;
}

/** Whether a session reads messages right now, as its machine reports it. */
export interface SessionInputStatus {
  sessionId: Id;
  /** Launched headless by this agent with stream-json input, process alive, stdin open. */
  accepting: boolean;
  /** Why not, as a sentence, when not accepting. */
  reason?: string;
  /** When the agent closes the input of an idle session (it then finishes and exits). */
  closesAt?: Ts | null;
  /** Claude Code is working on a message now (it has not answered the last one yet). */
  busy?: boolean;
}

/** GET /api/sessions/:id/messages */
export interface SessionMessagesResponse {
  messages: SessionMessage[];
  input: SessionInputStatus;
}

/** POST /api/sessions/:id/messages */
export interface SendMessageRequest {
  text: string;
}

export const HEADLESS_MESSAGE_MAX_CHARS = 20_000;
/** Messages kept per session by the hub. */
export const SESSION_MESSAGES_KEPT = 200;
/** An idle launched session's input is closed after this long without a message, so it can finish. */
export const SESSION_INPUT_IDLE_MS = 30 * 60_000;

/** state.ext['session-control'] */
export interface SessionControlState {
  claims: SessionClaim[];
  /**
   * The latest few messages of the most recently messaged sessions, oldest first, with long
   * text clipped to a preview (`clipped`). Enough to show live state; the full history of
   * one session is GET /api/sessions/:id/messages.
   */
  messages: Record<Id, SessionMessage[]>;
  /**
   * R-FN-6: launched sessions Orgus closed because they sat idle (the newest, bounded), by
   * session id. Absent when there are none. A wake resumes them from their saved state.
   */
  idleClosed?: Record<Id, SessionIdleClose>;
}

/** R-FN-6: the ended reason of a launched session Orgus closed after it sat idle. */
export const SESSION_ENDED_IDLE_CLOSED = 'idle-closed';

export interface SessionIdleClose {
  reason: typeof SESSION_ENDED_IDLE_CLOSED;
  at: Ts;
  /** How long it had been idle when Orgus closed it. */
  idleMs: number;
}

/**
 * R-FN-6: why this session must not be closed for idleness now, or null when it is due: only a
 * launched session, idle past `idleMs` (0: never), with nothing asked of a person (permission,
 * elicitation, question) and no wake waiting for it.
 */
export function idleCloseBlocker(s: Session, now: number, idleMs: number, queued: boolean): string | null {
  if (sessionOrigin(s) !== 'launched') return 'not launched by Orgus';
  if (!(idleMs > 0)) return 'idle close is off';
  if (s.status !== 'idle') return `it is ${s.status}`;
  if (s.state === 'waiting_for_human') return 'it waits for a person';
  const ext = s.ext as { pendingApprovals?: unknown; asks?: unknown } | undefined;
  if (typeof ext?.pendingApprovals === 'number' && ext.pendingApprovals > 0) return 'an approval waits';
  if (ext?.asks) return 'it asked a question';
  if (queued) return 'a wake is queued';
  if (now - s.lastActivity < idleMs) return 'not idle long enough';
  return null;
}

/** Whole minutes since the session last did anything (0 while it works or once it ended). For "idle for N min". */
export function idleMinutes(s: { status: string; lastActivity: Ts }, now: Ts): number {
  if (s.status !== 'idle') return 0;
  return Math.max(0, Math.floor((now - s.lastActivity) / 60_000));
}

// ---------------------------------------------------------------------------
// Events (the Events tab and the timeline scrubber)
// ---------------------------------------------------------------------------

export type SessionEventKind = 'prompt' | 'reply' | 'tool' | 'subagent' | 'approval' | 'compaction' | 'error' | 'system';

export const SESSION_EVENT_KINDS: readonly SessionEventKind[] = ['prompt', 'reply', 'tool', 'subagent', 'approval', 'compaction', 'error', 'system'];

export const SESSION_EVENT_KIND_LABEL: Record<SessionEventKind, string> = {
  prompt: 'Prompts',
  reply: 'Replies',
  tool: 'Tool calls',
  subagent: 'Subagents',
  approval: 'Approvals',
  compaction: 'Compactions',
  error: 'Errors',
  system: 'System',
};

/** A file edit carried by an Edit, Write or MultiEdit call, clipped, for replaying it as a diff. */
export interface SessionEdit {
  file: string;
  /** Text replaced ('' for a new file written whole). */
  before: string;
  after: string;
  /** The texts were clipped to SESSION_EDIT_MAX_CHARS. */
  clipped?: boolean;
}

export interface SessionEvent {
  /** Order within the session (the agent's read order). */
  seq: number;
  at: Ts | null;
  kind: SessionEventKind;
  /** One line: the prompt, the tool and what it was for, "Context compacted". */
  title: string;
  /** More text (a command, a reply, a summary), clipped. */
  detail?: string;
  /** Tool name for tool, subagent and approval events. */
  tool?: string;
  /** For tools: finished ok, failed, still running, or refused by a person or a rule. */
  status?: 'ok' | 'error' | 'pending' | 'denied' | 'allowed';
  /** Tool call to result, when both had a timestamp. */
  durationMs?: number;
  edit?: SessionEdit;
  /** Subagent type, or who decided an approval. */
  meta?: string;
}

/** GET /api/sessions/:id/events?after= */
export interface SessionEventsPage {
  sessionId: Id;
  events: SessionEvent[];
  /** Only the end of a long transcript was read; earlier events are not listed. */
  partial: boolean;
  /** Events dropped from the front because the list is capped. */
  omitted: number;
  /** A revision cursor: send it back as `after` to get only events new or changed since. */
  last: number;
  /**
   * The reader epoch that numbered these events (absent from older agents). seq and `last`
   * only mean something within one epoch: when it differs from the one a client holds, the
   * page carries the whole list and the client replaces what it has.
   */
  epoch?: string;
  at: Ts;
}

/** hub -> agent, ExtMessage { ext: 'session-control', type: 'events' | 'send' | 'input', id }. */
export interface SessionEventsRequest {
  sessionId: Id;
  /** Only events new or changed since this revision (an earlier page's `last`). */
  after?: number;
  /** The epoch `after` came from; when the reader's differs, `after` is ignored. */
  epoch?: string;
}
export interface SessionSendRequest {
  sessionId: Id;
  messageId: Id;
  text: string;
}
export interface SessionInputRequest {
  sessionId: Id;
  /** 'close': close the session's input now, so it finishes its work and exits. */
  op: 'status' | 'close';
}

/** agent -> hub, type 'result' (same id). */
export type SessionControlResult<T> = { ok: true; data: T } | { ok: false; error: UiErrorShape };

/** agent -> hub, type 'delivered': Claude Code echoed a message back (read it from stdin). */
export interface SessionDeliveredNotice {
  sessionId: Id;
  messageId: Id;
  at: Ts;
}

export const SESSION_EVENTS_MAX = 2000;
export const SESSION_EDIT_MAX_CHARS = 4000;

/** Audit action names this module records (not core ActionKinds). */
export type SessionControlAuditAction = 'session.claim' | 'session.release' | 'session.message' | 'session.input-close' | 'session.wake' | 'session.landed' | 'session.landed-stop' | 'session.idle-close';

export const SESSION_CONTROL_ACTION_LABEL: Record<SessionControlAuditAction, string> = {
  'session.claim': 'Claim session',
  'session.release': 'Release session',
  'session.message': 'Send message',
  'session.input-close': 'Close session input',
  'session.wake': 'Wake session',
  'session.landed': 'Branch landed',
  'session.landed-stop': 'Stop landed session',
  'session.idle-close': 'Close idle session',
};

/** POST /api/sessions/:id/landed: the landing queue landed this session's branch (ADR 0055). */
export interface SessionLandedRequest {
  branch: string;
  /** main's commit after the landing. */
  sha: string;
}

export interface SessionLandedResponse {
  /** What happened to the session's input: closed, already ended, or why it was not closed. */
  input: 'closed' | 'ended' | string;
  /**
   * The mission it belongs to: closed as done by this landing, closes when its sessions end,
   * already closed before this landing (already, closed false), or none.
   */
  mission: { id: string; closed: boolean; already?: boolean } | null;
}
